import React, { useState, useRef, useEffect } from 'react';
import CopilotResponseView from "./CopilotResponseView";
import { User, Lightbulb, Download, Copy, Check, RotateCcw } from "lucide-react";
import { saveAs } from "file-saver";
import { formatDateTimeOrNull } from "utils";
import APIService, { API_URL } from "services/apiservice";
import { AuthHeader } from "services/authheader";

// The download route sits behind the Passport auth:api guard, and a browser navigation
// sends no Authorization header, so a plain <a href download> would 401. Passport's
// cookie fallback is not wired in this app. Fetch with the header, then save the blob.
//
// The filename comes from the artifact, not from Content-Disposition: this is a
// cross-origin fetch and CorsMiddleware sets no Access-Control-Expose-Headers, so the
// browser cannot read that header at all.
// Rendering a long PD takes real server time, but an unbounded fetch means a dropped
// wifi connection leaves the button disabled on "Preparing..." forever, with a page
// reload as the only recovery. APIService.doRequest wraps every other call in the app
// with an AbortController for the same reason.
const DOWNLOAD_TIMEOUT_MS = 120000;

function DownloadArtifactButton({ artifact }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const controllerRef = useRef(null);

  // Clicking Download and then "New chat" unmounts this button mid-fetch, because
  // doNewChat replaces the whole results array. Without this, a large PDF keeps
  // downloading only to be discarded, and the fetch's own setBusy/setError land on a
  // component that no longer exists.
  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  const onClick = async () => {
    setBusy(true);
    setError(null);

    const controller = new AbortController();
    controllerRef.current = controller;
    const timer = setTimeout(() => controller.abort(), DOWNLOAD_TIMEOUT_MS);

    try {
      const res = await fetch(`${API_URL}${artifact.url}`, {
        headers: AuthHeader(),
        signal: controller.signal,
      });

      // The rest of the app treats 401 and 419 as session expiry
      // (APIService.handleResponse, src/services/apiservice.js:121-126), which logs
      // out through this same singleton: clears the token cookie, the user-info
      // cookie, the expiry cookie and localStorage. Without it a recruiter whose
      // token expires here sees the message but stays apparently logged in
      // everywhere else, with stale cookies.
      if (res.status === 401 || res.status === 419) {
        APIService.handleLogout();
        setError("Your session has expired. Please sign in again.");
        return;
      }
      if (!res.ok) throw new Error(`download failed with ${res.status}`);

      saveAs(await res.blob(), artifact.filename);
    } catch (e) {
      setError(
        e.name === "AbortError"
          ? "That took too long and could not be downloaded. Please try again."
          : "That could not be downloaded. Please try again."
      );
    } finally {
      clearTimeout(timer);
      // Clears the ref once the request has actually finished, so the unmount
      // cleanup above cannot abort a controller that is no longer in flight.
      controllerRef.current = null;
      setBusy(false);
    }
  };

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        data-testid="download-artifact"
        // The role name carries the title so a screen reader user knows WHICH document
        // this is, which matters once a conversation holds five drafts.
        aria-label={`Download PDF: ${artifact.title}`}
        className="flex items-center justify-center gap-2 rounded-lg border border-emerald-600 px-3 py-2 text-[14px] font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-emerald-500"
      >
        <Download className="w-4 h-4" />
        <span>{busy ? "Preparing..." : `Download PDF: ${artifact.title}`}</span>
      </button>
      {error && (
        <p role="alert" className="mt-1 text-[12px] text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

const COPIED_FEEDBACK_MS = 2000;

/**
 * Writes two flavours of the same reply.
 *
 * text/html is what Word, Outlook and Google Docs actually paste, so a copied answer
 * keeps its headings, bold and salary tables instead of arriving as one grey block.
 * text/plain is the markdown source rather than the flattened visible text, because
 * "**Overview**" pasted into a plain editor still carries the structure, and
 * "Overview" does not.
 *
 * Returns false if nothing reached the clipboard, so the caller can say so rather
 * than showing a "Copied" that did not happen.
 */
async function copyMessage(markdown, html) {
  const canWriteRich =
    html && typeof ClipboardItem !== "undefined" && navigator.clipboard?.write;

  if (canWriteRich) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([markdown], { type: "text/plain" }),
        }),
      ]);
      return true;
    } catch (e) {
      // Falls through to plain text. A denied permission, a browser that refuses
      // text/html, or a document that was not focused all land here, and plain
      // markdown on the clipboard beats nothing at all.
    }
  }

  try {
    await navigator.clipboard.writeText(markdown);
    return true;
  } catch (e) {
    return false;
  }
}

function CopyMessageButton({ markdown, bubbleRef }) {
  const [state, setState] = useState("idle");
  const timerRef = useRef(null);

  // The panel unmounts the whole message list on "New chat", so a pending reset
  // would otherwise fire setState on a component that no longer exists.
  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  const onClick = async () => {
    // Read the rendered markdown straight off the DOM. Re-rendering it to a string
    // here would mean a second react-markdown pipeline that could drift from what
    // the user is actually looking at.
    const html = bubbleRef.current?.querySelector(".copilot-response-view")?.innerHTML;
    const ok = await copyMessage(markdown, html);

    setState(ok ? "copied" : "failed");
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setState("idle"), COPIED_FEEDBACK_MS);
  };

  const label =
    state === "copied" ? "Copied" : state === "failed" ? "Could not copy" : "Copy";

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="copy-message"
      // The visible label already changes to "Copied", but a screen reader user is
      // not watching it, so the state goes in the accessible name too.
      aria-label={`${label} this reply`}
      className="flex items-center gap-1 rounded-md px-2 h-8 text-[12px] text-muted-foreground hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
    >
      {state === "copied" ? (
        <Check className="w-3.5 h-3.5 text-emerald-600" />
      ) : (
        <Copy className="w-3.5 h-3.5" />
      )}
      <span>{label}</span>
    </button>
  );
}

// onRetry is a PROP, not a useDispatch. Every test in ChatMessageView.test.jsx
// renders this component bare with no redux Provider, and ChatEli is already
// connected, so the callback comes down from there. Default noop keeps the bare
// renders working.
function ChatMessageView({ chat, onRetry = () => {} }) {
  const bubbleRef = useRef(null);

  // Only Eli's replies are worth copying, and only once they have finished
  // streaming: mid-stream the bubble holds half an answer, and a "Copied" on half
  // an answer is worse than no button. `streaming` is cleared by the reducer when
  // the stream ends (redux/copilot/copilotState.jsx).
  const showCopy = chat.type !== 'user' && !chat?.data?.streaming && !!chat?.data?.message;

  // Only on a failed turn, and only when there is something to re-send. A retry
  // with no payload would fire an empty request.
  const showRetry = chat.type === 'error' && !!chat?.data?.retry;

  // doRetryAgentChat only pops an error bubble once it is the LAST item in
  // results, and doAgentChat (sending a new message) never pops a stale one, so
  // two failed turns can leave two error bubbles on screen at once, each with
  // this button. A screen reader's rotor list strips surrounding prose, so a
  // fixed label would read identically for both with no way to tell which one
  // recovers which turn. The visible text stays plain "Try again" regardless:
  // the bubble is 400px wide and the visible label must not grow with the
  // message.
  const retryMessage = chat?.data?.retry?.message;
  const truncatedRetryMessage = retryMessage
    ? (String(retryMessage).length > 40 ? `${String(retryMessage).slice(0, 40)}...` : String(retryMessage))
    : null;
  const retryLabel = truncatedRetryMessage
    ? `Try sending "${truncatedRetryMessage}" again`
    : 'Try sending this message again';

  return (
    <div className={`flex w-full gap-3 my-4 ${chat.type === 'user' ? 'flex-row-reverse' : '' }`}>
      <div className={`p-2 h-8 w-8 flex items-center justify-center bg-text rounded-full text-white
          ${chat.type === 'user' ?
           'bg-gradient-to-br from-emerald-500 to-teal-500' : 'bg-gradient-to-br from-emerald-500 via-green-600 to-purple-600'}
          `}>
        {chat.type === 'user' ?
          <User />
          : <Lightbulb />
        }
      </div>
      {/*
        w-[70%] was a FIXED width, not a cap, so a two-word reply rendered as a
        755px gradient bar in the expanded panel. max-w + w-fit makes the bubble
        hug its content and stop at 70%.

        min-w-0 on this column and on the bubble below: both are flex items, and a
        flex item defaults to min-width:auto, so it cannot shrink below its
        min-content width. Tables, <pre> blocks and the raw Resources URLs the
        prompt mandates all have a large min-content, which is how long replies
        painted outside the rounded background in the collapsed 400px panel.

        Do NOT add justify-end to the row above. With flex-row-reverse the main-end
        is the LEFT, so the user bubble jumps to the far left. The default
        flex-start already packs it right.
      */}
      <div className="max-w-[70%] min-w-0 w-fit">
        <div
          ref={bubbleRef}
          role={chat.type === 'error' ? 'alert' : undefined}
          className={`flex w-full min-w-0 overflow-hidden rounded-xl pt-2 pb-2 px-3 shadow-md ${chat.type === 'user' ? 'items-center justify-end bg-gradient-to-br from-emerald-500 to-teal-500 text-white' : ''}`}
        >
            <CopilotResponseView message={chat?.data?.message}/>
        </div>
        {chat?.data?.artifact && <DownloadArtifactButton artifact={chat.data.artifact} />}
        {/*
          Copy shares the timestamp's row rather than taking one of its own. The
          collapsed panel is 400px tall and Wy's other complaint was that it is
          cramped, so a control that costs no extra vertical space is the right
          trade even though it makes this row taller than a bare timestamp.
        */}
        <div className={`flex items-center gap-2 mt-1 px-1 ${chat.type === 'user' ? 'justify-end' : ''}`}>
          {showRetry && (
            <button
              type="button"
              data-testid="eli-retry"
              aria-label={retryLabel}
              // The payload came out of redux state, which redux-toolkit freezes
              // with Immer in development. Copy it before it travels back through
              // a dispatch, so nothing downstream can trip over a frozen object.
              // The id identifies WHICH bubble this is: two failed turns can be
              // on screen at once, and the reducer needs to know which one to
              // remove rather than assuming it is whichever is last.
              onClick={() => onRetry({ ...chat.data.retry }, chat.data.id)}
              // Deliberately the same shape as CopyMessageButton, its neighbour in
              // this row: h-8, px-2, 12px text, w-3.5 icon, same hover and focus
              // ring. Only the text colour differs, because unlike Copy this is the
              // recovery path for a failed turn and has to read as the thing to do.
              className="flex items-center gap-1 rounded-md px-2 h-8 text-[12px] text-emerald-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Try again</span>
            </button>
          )}
          {showCopy && <CopyMessageButton markdown={chat.data.message} bubbleRef={bubbleRef} />}
          <p className="text-[10px] text-muted-foreground">
            {formatDateTimeOrNull(chat?.data?.timestamp, 'YYYY-MM-DD hh:mm A')}
          </p>
        </div>
      </div>
    </div>
  );
}

export default ChatMessageView;
